import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import {
  getScreenerTickers,
  addScreenerTicker,
  removeScreenerTicker,
  getScreenerData,
  getScreenerOIData,
  updateScreenerSettings,
  type ScreenerMetric,
} from '@/lib/api'
import type {
  ScreenerTicker,
  ScreenerSettings,
  ScreenerTickerData,
  ScreenerCandle,
} from '@/types'
import { FALLBACK_EXCHANGES } from '@/types'
import { finiteRevisionValue, monotonicOISourceAge, compareOIRevision, exchangeOIRevision, hasOIRevision,
  mergeFundingSnapshot, mergeVolumeHistory, mergeTradeCharts, mergeOIHistory, mergeScreenerTail } from '@/lib/screenerDataMerge'
import { ScreenerResponseCache } from '@/lib/screenerResponseCache'
import { bufferedHistoryRange, initialHistoryRange, SCREENER_INTERVAL_SECONDS } from '@/lib/screenerHistory'

interface UseScreenerDataResult {
  // State
  tickers: ScreenerTicker[]
  hasLoadedTickers: boolean
  activeTicker: string | null
  activeTickerData: ScreenerTickerData | null
  settings: ScreenerSettings
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null

  // Actions
  addTicker: (symbol: string) => Promise<boolean>
  removeTicker: (symbol: string) => Promise<boolean>
  setActiveTicker: (symbol: string | null) => void
  updateSettings: (settings: Partial<ScreenerSettings>) => Promise<boolean>
  refresh: () => Promise<void>
}

const DEFAULT_SETTINGS: ScreenerSettings = {
  period: '1m',
  exchanges: FALLBACK_EXCHANGES,
  markets: ['spot', 'futures'],
  chart_filters: {
    exchanges: FALLBACK_EXCHANGES,
    markets: ['spot', 'futures'],
  },
  orderbook_filters: {
    exchanges: FALLBACK_EXCHANGES,
    markets: ['spot', 'futures'],
  },
  candle_limit: 200,
}

const POLL_INTERVAL = 5000 // 5 seconds
const OI_DELTA_POLL_INTERVAL = 1000 // observe committed buckets promptly
const CVD_SNAPSHOT_INTERVAL = 15_000
const FAST_CVD_SNAPSHOT_INTERVAL = 2000
const FAST_CVD_MAX_POINTS = 500
const DEFAULT_METRICS: ScreenerMetric[] = ['price', 'oi', 'volume', 'cvd', 'funding']

function normalizedContextValues(values: string[] | undefined): string[] {
  return (values || [])
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean)
    .sort()
}

function screenerChartContextKey(
  activeTicker: string | null,
  settings: ScreenerSettings,
  includeOIOhlc: boolean,
): string {
  const timeframe = settings.timeframe
  return JSON.stringify({
    ticker: activeTicker?.trim().toUpperCase() || null,
    period: settings.period,
    includeOIOhlc,
    timeframe: {
      mode: timeframe?.mode ?? 'live',
      relativeSeconds: timeframe?.relative_seconds ?? null,
      from: timeframe?.from_ts ?? null,
      to: timeframe?.to_ts ?? null,
    },
    candleLimit: settings.candle_limit,
    chartExchanges: normalizedContextValues(
      settings.chart_filters?.exchanges ?? settings.exchanges,
    ),
    chartMarkets: normalizedContextValues(
      settings.chart_filters?.markets ?? settings.markets,
    ),
  })
}

export function useScreenerData(includeOrderbook = false, includeOIOhlc = false,
  metrics: ScreenerMetric[] = DEFAULT_METRICS): UseScreenerDataResult {
  const [tickers, setTickers] = useState<ScreenerTicker[]>([])
  const [hasLoadedTickers, setHasLoadedTickers] = useState(false)
  const [activeTicker, setActiveTicker] = useState<string | null>(null)
  const [activeTickerData, setRawActiveTickerData] = useState<ScreenerTickerData | null>(null)
  const [settings, setSettings] = useState<ScreenerSettings>(DEFAULT_SETTINGS)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preferenceError, setPreferenceError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const metricKey = [...metrics].sort().join(',')
  const chartDataContextKey = `${screenerChartContextKey(activeTicker, settings, includeOIOhlc)}:${metricKey}`
  const dataQueryContextKey = JSON.stringify({
    chart: chartDataContextKey,
    orderbookExchanges: normalizedContextValues(settings.orderbook_filters?.exchanges),
    orderbookMarkets: normalizedContextValues(settings.orderbook_filters?.markets),
    includeOrderbook,
  })

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const oiPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fullAbortRef = useRef<AbortController | null>(null)
  const fullRequestRef = useRef<{ context: string; promise: Promise<void>; auxiliary: boolean; repair: boolean } | null>(null)
  const oiAbortRef = useRef<AbortController | null>(null)
  const fullSequenceRef = useRef(0)
  const oiSequenceRef = useRef(0)
  const activeQueryContextRef = useRef<string | null>(null)
  const activeDataContextRef = useRef<string | null>(null)
  const dataRef = useRef<ScreenerTickerData | null>(null)
  const responseCacheRef = useRef(new ScreenerResponseCache())
  const settingsRevisionRef = useRef(0)
  const settingsIntentRef = useRef(DEFAULT_SETTINGS)
  const settingsQueueRef = useRef<Promise<unknown>>(Promise.resolve())
  const lastCvdSnapshotRef = useRef(0)
  const lastOrderbookSnapshotRef = useRef(0)
  const lastCacheWriteRef = useRef(0)
  const lastAuxiliaryFailureRef = useRef(0)
  const lastTailAtRef = useRef(0)
  const lastReconciliationRef = useRef(0)
  const priceFallbackNeededRef = useRef(false)
  const optionalMetricsNeededRef = useRef<ScreenerMetric[]>([])
  const orderbookInitialPendingRef = useRef(false)
  const enrichmentImmediateRef = useRef(false)
  const lastCorrectionRepairRef = useRef(0)
  const historyInvalidationGenerationRef = useRef(0)
  const historyInvalidationsRef = useRef(new Map<string, { generation: number; from_ts?: number | null }>())
  const setActiveTickerData = useCallback((update: ScreenerTickerData | null
    | ((current: ScreenerTickerData | null) => ScreenerTickerData | null)) => {
    setRawActiveTickerData(current => {
      const next = typeof update === 'function' ? update(current) : update
      dataRef.current = next
      return next
    })
  }, [chartDataContextKey])
  const chartScopeActive = (settings.chart_filters?.exchanges ?? settings.exchanges).length > 0
    && (settings.chart_filters?.markets ?? settings.markets ?? []).length > 0
  const includesNow = (settings.timeframe?.mode ?? 'live') !== 'absolute'
    || ((settings.timeframe?.from_ts ?? 0) <= Date.now() / 1000
      && (settings.timeframe?.to_ts ?? 0) >= Date.now() / 1000 - (SCREENER_INTERVAL_SECONDS[settings.period] || 60) * 2)
  const cvdSnapshotCadence = useCallback(() => {
    const effectiveInterval = dataRef.current?.oi_interval || dataRef.current?.price_interval || settings.period
    if (effectiveInterval !== '5s') return CVD_SNAPSHOT_INTERVAL
    const window = settings.timeframe?.mode && settings.timeframe.mode !== 'live'
      ? bufferedHistoryRange(initialHistoryRange(null, effectiveInterval, settings.timeframe)) : null
    const points = window ? Math.ceil((window.to - window.from) / 5) + 1 : settings.candle_limit
    return points <= FAST_CVD_MAX_POINTS ? FAST_CVD_SNAPSHOT_INTERVAL : CVD_SNAPSHOT_INTERVAL
  }, [settings.period, settings.timeframe, settings.candle_limit])
  // Fetch tickers list and settings
  const fetchTickers = useCallback(async (includeSettings = true, signal?: AbortSignal) => {
    const requestedRevision = settingsRevisionRef.current
    try {
      const result = await getScreenerTickers(signal)
      if (signal?.aborted) return 'stop'
      setTickers(result.tickers || [])
      if (includeSettings && result.settings && requestedRevision === settingsRevisionRef.current) {
        const incomingSettings = result.settings
        settingsIntentRef.current = incomingSettings
        setSettings((current) => JSON.stringify(current) === JSON.stringify(incomingSettings)
          ? current : incomingSettings)
      }
      setHasLoadedTickers(true)
      setError(null)
      return 'loaded'
    } catch (err) {
      if (signal?.aborted) return 'stop'
      console.error('Failed to fetch screener tickers:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch tickers')
      const status = err && typeof err === 'object' && 'status' in err ? err.status : undefined
      if ((err instanceof Error && (err.message === 'Unauthorized' || err.name === 'AbortError'))
        || (typeof status === 'number' && status < 500 && status !== 408 && status !== 429)) return 'stop'
      return 'retry'
    }
  }, [])

  // Fetch data for active ticker
  const loadActiveTickerData = useCallback(async (signal: AbortSignal, sequence: number, auxiliary = false, repair = false) => {
    if (!activeTicker) return
    const cvdDue = metrics.includes('cvd') && includesNow && chartScopeActive
      && Date.now() - lastCvdSnapshotRef.current >= cvdSnapshotCadence()
    const orderbookDue = auxiliary && includeOrderbook && (orderbookInitialPendingRef.current
      || Date.now() - lastOrderbookSnapshotRef.current >= POLL_INTERVAL)
    const reconcileDue = includesNow && chartScopeActive && Date.now() - lastReconciliationRef.current >= 60_000
    const reconcileOI = reconcileDue && dataRef.current?.oi_revision == null
    const enrichPrice = auxiliary && priceFallbackNeededRef.current
    const snapshotMetrics = !auxiliary && !repair
      ? metrics.filter(metric => metric !== 'cvd' && metric !== 'volume')
      : !auxiliary || reconcileOI ? metrics : metrics.filter(metric => optionalMetricsNeededRef.current.includes(metric)
        || (metric === 'cvd' && cvdDue) || (metric === 'price' && enrichPrice)
        || (reconcileDue && (metric === 'price' || metric === 'volume')))
    if (auxiliary && !snapshotMetrics.length && !orderbookDue) return
    if (enrichPrice) priceFallbackNeededRef.current = false
    if (auxiliary) {
      orderbookInitialPendingRef.current = false
      enrichmentImmediateRef.current = false
    }
    const isCurrent = () => !signal.aborted && sequence === fullSequenceRef.current
      && activeQueryContextRef.current === dataQueryContextKey
    try {
      const loadedWindow = auxiliary && settings.timeframe?.mode === 'absolute' ? dataRef.current?.history_window : undefined
      const historicalWindow = loadedWindow
        ? { from: loadedWindow.from_ts, to: includesNow ? Math.floor(Date.now() / 1000) : loadedWindow.to_ts }
        : settings.timeframe && settings.timeframe.mode !== 'live'
          ? bufferedHistoryRange(initialHistoryRange(null, settings.period, settings.timeframe)) : undefined
      if (!auxiliary && historicalWindow && historicalWindow.to <= historicalWindow.from) {
        setActiveTickerData({ symbol: activeTicker, updated_at: null, available_exchanges: [], exchanges: {},
          history_window: { from_ts: historicalWindow.from, to_ts: historicalWindow.to,
            earliest_ts: null, latest_ts: null, has_earlier: false, has_later: false, limit: 5000, truncated: false } })
        activeDataContextRef.current = chartDataContextKey
        return
      }
      const result = await getScreenerData(
        activeTicker,
        settings.timeframe,
        auxiliary ? dataRef.current?.oi_interval || settings.period : settings.period,
        orderbookDue,
        signal,
        (settings.chart_filters?.markets ?? settings.markets)?.length === 0
          ? [] : settings.chart_filters?.exchanges ?? settings.exchanges ?? [],
        historicalWindow,
        includeOIOhlc,
        { markets: settings.chart_filters?.markets ?? settings.markets ?? [],
          includeFallback: enrichPrice,
          metrics: snapshotMetrics,
          limit: auxiliary && snapshotMetrics.includes('cvd') && cvdSnapshotCadence() === FAST_CVD_SNAPSHOT_INTERVAL
            ? historicalWindow ? FAST_CVD_MAX_POINTS : Math.min(FAST_CVD_MAX_POINTS, settings.candle_limit)
            : historicalWindow ? 5000 : settings.candle_limit,
          orderbookExchanges: settings.orderbook_filters?.exchanges ?? settings.exchanges,
          orderbookMarkets: settings.orderbook_filters?.markets ?? settings.markets ?? [] },
      )
      if (!isCurrent()) return
      if (result.ok && result.data) {
        if (auxiliary) lastAuxiliaryFailureRef.current = 0
        const invalidation = historyInvalidationsRef.current.get(activeTicker.toUpperCase())
        const incoming = invalidation ? { ...result.data, history_invalidation: invalidation } : result.data
        if (snapshotMetrics.includes('oi')) incoming.oi_read_started_at = incoming.read_started_at
        if (!auxiliary) priceFallbackNeededRef.current = metrics.includes('price')
          && Object.values(incoming.exchanges).some(venue => venue.candle_source === 'unavailable')
        if (!auxiliary) {
          optionalMetricsNeededRef.current = metrics.filter(metric => (metric === 'cvd' || metric === 'volume')
            && !snapshotMetrics.includes(metric)
            && !Object.values(incoming.exchanges).some(venue => metric === 'cvd'
              ? venue.cvd_history !== undefined : venue.volume_history !== undefined))
          incoming.pending_metrics = optionalMetricsNeededRef.current
          orderbookInitialPendingRef.current = includeOrderbook
          enrichmentImmediateRef.current = true
        }
        if (snapshotMetrics.includes('cvd') || Object.values(incoming.exchanges).some(venue => venue.cvd_history !== undefined)) {
          lastCvdSnapshotRef.current = Date.now()
        }
        if (orderbookDue) lastOrderbookSnapshotRef.current = Date.now()
        if (!auxiliary || reconcileDue) lastReconciliationRef.current = Date.now()
        if (!auxiliary && incoming.oi_pending_overflow) lastCorrectionRepairRef.current = Date.now()
        if (auxiliary && !reconcileOI) {
          optionalMetricsNeededRef.current = optionalMetricsNeededRef.current.filter(metric => !snapshotMetrics.includes(metric))
          setActiveTickerData(current => {
            if (!current) return current
            const next = { ...mergeScreenerTail(current, incoming, { replaceCvd: true, allowNewExchanges: true }),
              pending_metrics: (current.pending_metrics || []).filter(metric => !snapshotMetrics.includes(metric as ScreenerMetric)) }
            if ((current.pending_metrics?.length || 0) > 0 || Date.now() - lastCacheWriteRef.current >= 15_000) {
              responseCacheRef.current.set(chartDataContextKey, next, Date.now())
              lastCacheWriteRef.current = Date.now()
            }
            return next
          })
          setError(null)
          return
        }
        responseCacheRef.current.set(chartDataContextKey, incoming, Date.now())
        lastCacheWriteRef.current = Date.now()
        lastTailAtRef.current = Date.now()
        const canMergeCurrent = !repair && activeDataContextRef.current === chartDataContextKey
          && (settings.timeframe?.mode ?? 'live') === 'live'
          && !(incoming.oi_revision != null && dataRef.current?.oi_revision != null
            && incoming.oi_revision < dataRef.current.oi_revision)
          && !(incoming.oi_revision_epoch && dataRef.current?.oi_revision_epoch
            && incoming.oi_revision_epoch !== dataRef.current.oi_revision_epoch)
        setActiveTickerData((current) => {
          if (
            !canMergeCurrent
            || !current
            || current.symbol.toUpperCase() !== incoming.symbol.toUpperCase()
          ) {
            return incoming
          }
          const exchanges = { ...incoming.exchanges }
          for (const [exchange, currentData] of Object.entries(current.exchanges)) {
            const incomingData = exchanges[exchange]
            if (!incomingData) {
              exchanges[exchange] = currentData
              continue
            }
            const mergedHistory = mergeOIHistory(
              currentData.oi_history,
              incomingData.oi_history,
            )
            const revisionComparison = compareOIRevision(
              exchangeOIRevision(currentData),
              exchangeOIRevision(incomingData),
            )
            if (
              hasOIRevision(currentData)
              && revisionComparison >= 0
            ) {
              exchanges[exchange] = {
                ...incomingData,
                ...mergeFundingSnapshot(currentData, incomingData),
                ...mergeVolumeHistory(currentData, incomingData, mergedHistory),
                oi_current: currentData.oi_current,
                oi_current_usdt: currentData.oi_current_usdt,
                oi_history: mergedHistory,
                oi_updated_at: currentData.oi_updated_at,
                oi_observed_at: currentData.oi_observed_at,
                oi_read_revision: currentData.oi_read_revision,
                oi_quality: currentData.oi_quality,
                oi_sample_kind: currentData.oi_sample_kind,
                // A response with the same OI revision can still carry a
                // newer source-age calculation.  Keeping the older age made
                // a stopped collector look permanently fresh in the UI.
                oi_source_age_seconds: revisionComparison === 0
                  ? monotonicOISourceAge(
                      currentData.oi_source_age_seconds,
                      incomingData.oi_source_age_seconds,
                    )
                  : currentData.oi_source_age_seconds,
              }
            } else {
              exchanges[exchange] = {
                ...incomingData,
                ...mergeFundingSnapshot(currentData, incomingData),
                ...mergeVolumeHistory(currentData, incomingData, mergedHistory),
                oi_history: mergedHistory,
              }
            }
            Object.assign(exchanges[exchange], mergeTradeCharts(currentData, incomingData))
          }
          return {
            ...incoming,
            read_started_at: Math.max(incoming.read_started_at || 0, current.read_started_at || 0) || undefined,
            oi_read_started_at: Math.max(incoming.oi_read_started_at || 0, current.oi_read_started_at || 0) || undefined,
            oi_authoritative_window: (current.oi_read_started_at || 0) > (incoming.oi_read_started_at || 0)
              ? current.oi_authoritative_window : undefined,
            updated_at: Math.max(incoming.updated_at || 0, current.updated_at || 0) || null,
            oi_history_until: Math.max(
              finiteRevisionValue(incoming.oi_history_until),
              finiteRevisionValue(current.oi_history_until),
            ) || null,
            exchanges,
          }
        })
        activeDataContextRef.current = chartDataContextKey
        const incomingLastUpdated = result.data.updated_at
          ? new Date(result.data.updated_at * 1000)
          : new Date()
        setLastUpdated((current) => (
          current && current.getTime() >= incomingLastUpdated.getTime()
            ? current
            : incomingLastUpdated
        ))
        setError(null)
      } else {
        if (auxiliary) lastAuxiliaryFailureRef.current = Date.now()
        setError(result.error || 'Failed to fetch ticker data')
      }
    } catch (err) {
      if (!isCurrent()) return
      if (auxiliary) lastAuxiliaryFailureRef.current = Date.now()
      console.error('Failed to fetch ticker data:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch ticker data')
    }
  }, [
    activeTicker,
    settings.timeframe,
    settings.period,
    includeOrderbook,
    includeOIOhlc,
    settings.chart_filters?.exchanges,
    settings.chart_filters?.markets,
    settings.exchanges,
    settings.markets,
    dataQueryContextKey,
    chartDataContextKey,
    metricKey,
    includesNow,
    chartScopeActive,
    settings.candle_limit,
    cvdSnapshotCadence,
  ])

  const fetchActiveTickerData = useCallback((auxiliary = false, repair = false): Promise<void> => {
    if (!activeTicker) {
      setActiveTickerData(null)
      return Promise.resolve()
    }
    // The refresh button and scheduled poll share one request. Every full read
    // belongs to the view, including reads started manually before switching coins.
    if (fullRequestRef.current?.context === dataQueryContextKey && !fullAbortRef.current?.signal.aborted
      && (auxiliary || !fullRequestRef.current.auxiliary) && (!repair || fullRequestRef.current.repair)) {
      return fullRequestRef.current.promise
    }
    fullAbortRef.current?.abort()
    const controller = new AbortController()
    fullAbortRef.current = controller
    const sequence = ++fullSequenceRef.current
    const promise = loadActiveTickerData(controller.signal, sequence, auxiliary, repair).finally(() => {
      if (fullRequestRef.current?.promise === promise) fullRequestRef.current = null
    })
    fullRequestRef.current = { context: dataQueryContextKey, promise, auxiliary, repair }
    return promise
  }, [activeTicker, dataQueryContextKey, loadActiveTickerData])

  const fetchActiveOIDelta = useCallback(async (signal?: AbortSignal) => {
    if (!activeTicker || activeDataContextRef.current !== chartDataContextKey || !includesNow
      || (settings.chart_filters?.markets ?? settings.markets)?.length === 0
      || (settings.chart_filters?.exchanges ?? settings.exchanges)?.length === 0) return
    const tailMetrics = metrics.filter(metric => metric !== 'cvd')
    if (!tailMetrics.length) return
    const current = dataRef.current
    if (!current) return
    const interval = current.oi_interval || current.price_interval || settings.period
    const step = SCREENER_INTERVAL_SECONDS[interval] || 60
    const timestamps = Object.values(current.exchanges).flatMap(venue => (venue.oi_history || []).map(point => point.timestamp))
    const historyFrom = current.history_window?.from_ts ?? (timestamps.length ? Math.min(...timestamps) : undefined)
    // After a long suspension the missing range may exceed the bounded tail.
    // Repair once with a snapshot; never silently bridge missing buckets.
    if (lastTailAtRef.current && Date.now() - lastTailAtRef.current > step * 12 * 1000) {
      await fetchActiveTickerData(false, true)
      return
    }
    const sequence = ++oiSequenceRef.current
    try {
      const result = await getScreenerOIData(activeTicker, interval, signal,
        settings.chart_filters?.exchanges ?? settings.exchanges, includeOIOhlc,
        { limit: 12, markets: settings.chart_filters?.markets ?? settings.markets ?? [], metrics: tailMetrics,
          historyFrom,
          sinceRevision: current.oi_revision ?? undefined,
          sinceEpoch: current.oi_revision_epoch ?? undefined,
          to: Math.floor(Math.min(Date.now() / 1000, settings.timeframe?.mode === 'absolute'
            ? settings.timeframe.to_ts ?? Infinity : Infinity)) })
      if (signal?.aborted || sequence !== oiSequenceRef.current
        || activeQueryContextRef.current !== dataQueryContextKey
        || activeDataContextRef.current !== chartDataContextKey) return
      if (!result.ok || !result.data) {
        if (result.error) setError(result.error)
        return
      }
      const delta = result.data
      const revisionReset = delta.oi_revision != null && current.oi_revision != null
        && delta.oi_revision < current.oi_revision
      const epochReset = delta.oi_revision_epoch && current.oi_revision_epoch
        && delta.oi_revision_epoch !== current.oi_revision_epoch
      const pendingChanged = delta.oi_pending_signature !== undefined
        && delta.oi_pending_signature !== current.oi_pending_signature
      const oldPendingCorrection = pendingChanged && delta.oi_pending_from != null && delta.update_window
        && (historyFrom === undefined || delta.oi_pending_from >= historyFrom)
        && delta.oi_pending_from < delta.update_window.from_ts
      const overflowDue = delta.oi_pending_overflow
        && Date.now() - lastCorrectionRepairRef.current >= 60_000
      if (revisionReset || epochReset || oldPendingCorrection || overflowDue || (delta.oi_invalidated_from != null && delta.update_window
        && (historyFrom === undefined || delta.oi_invalidated_from >= historyFrom)
        && delta.oi_invalidated_from < delta.update_window.from_ts)) {
        // A failed repair must not turn a one-second live poll into repeated
        // full-window work. Keep the old revision until a repair succeeds.
        if (Date.now() - lastCorrectionRepairRef.current < POLL_INTERVAL) return
        responseCacheRef.current.clear()
        lastCorrectionRepairRef.current = Date.now()
        const correctionFrom = [delta.oi_invalidated_from, oldPendingCorrection ? delta.oi_pending_from : null]
          .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
        historyInvalidationsRef.current.set(activeTicker.toUpperCase(), {
          generation: ++historyInvalidationGenerationRef.current,
          from_ts: revisionReset || epochReset || overflowDue || !correctionFrom.length ? null : Math.min(...correctionFrom),
        })
        await fetchActiveTickerData(false, true)
        return
      }
      lastTailAtRef.current = Date.now()
      const oiAuthority = tailMetrics.includes('oi') && delta.oi_revision != null
        && delta.read_started_at != null && delta.update_window
        ? { ...delta.update_window, read_started_at: delta.read_started_at } : undefined
      setActiveTickerData(existing => existing ? mergeScreenerTail(existing,
        { ...existing, ...delta, oi_authoritative_window: oiAuthority, exchanges: delta.exchanges } as ScreenerTickerData,
        { maxPoints: (settings.timeframe?.mode ?? 'live') === 'live' ? Math.max(500, settings.candle_limit) : 5000,
          from: settings.timeframe?.mode === 'relative'
            ? Date.now() / 1000 - (settings.timeframe.relative_seconds || 0) - 900
            : settings.timeframe?.from_ts,
          to: settings.timeframe?.mode === 'absolute' ? settings.timeframe.to_ts : undefined,
          replaceCvd: false }) : existing)
      if (delta.updated_at) setLastUpdated(previous => {
        const updated = new Date(delta.updated_at! * 1000)
        return previous && previous >= updated ? previous : updated
      })
      setError(delta.trade_charts_error || null)
    } catch (err) {
      if (signal?.aborted || sequence !== oiSequenceRef.current
        || activeQueryContextRef.current !== dataQueryContextKey) return
      setError(err instanceof Error ? err.message : 'Failed to fetch live chart data')
    }
  }, [activeTicker, settings.period, settings.candle_limit, settings.timeframe,
    settings.chart_filters?.exchanges, settings.chart_filters?.markets, settings.exchanges, settings.markets,
    includeOIOhlc, dataQueryContextKey, chartDataContextKey, includesNow, metricKey, fetchActiveTickerData])

  // Full refresh
  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      // The view already owns its settings. Rehydrating them here can restart
      // polling midway through refresh and restore another tab's preferences.
      await Promise.all([fetchTickers(false), fetchActiveTickerData()])
    } finally {
      setIsLoading(false)
    }
  }, [fetchTickers, fetchActiveTickerData, activeTicker])

  // Add ticker
  const addTicker = useCallback(async (symbol: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const result = await addScreenerTicker(
        symbol.toUpperCase(),
        settings.chart_filters?.exchanges || settings.exchanges
      )
      if (result.ok) {
        await fetchTickers(false)
        // Auto-select backend-normalized symbol if available.
        const normalizedSymbol = (result.symbol || symbol).toUpperCase()
        setActiveTicker(normalizedSymbol)
        return true
      } else {
        setError(result.error || 'Failed to add ticker')
        return false
      }
    } catch (err) {
      console.error('Failed to add ticker:', err)
      setError(err instanceof Error ? err.message : 'Failed to add ticker')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [settings.chart_filters?.exchanges, settings.exchanges, fetchTickers])

  // Remove ticker
  const removeTicker = useCallback(async (symbol: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const result = await removeScreenerTicker(symbol)
      if (result.ok) {
        // If removing the active ticker, switch to another one
        if (activeTicker === symbol) {
          const remaining = tickers.filter(t => t.symbol !== symbol)
          setActiveTicker(remaining.length > 0 ? remaining[0].symbol : null)
        }
        await fetchTickers(false)
        return true
      } else {
        setError(result.error || 'Failed to remove ticker')
        return false
      }
    } catch (err) {
      console.error('Failed to remove ticker:', err)
      setError(err instanceof Error ? err.message : 'Failed to remove ticker')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [activeTicker, tickers, fetchTickers])

  // Local intent is immediate. Ordered saves cannot let an older response
  // restore a superseded interval, market, or date range.
  const updateSettingsAction = useCallback((newSettings: Partial<ScreenerSettings>): Promise<boolean> => {
    const revision = ++settingsRevisionRef.current
    const desiredSettings = { ...settingsIntentRef.current, ...newSettings }
    settingsIntentRef.current = desiredSettings
    setSettings(desiredSettings)
    setPreferenceError(null)
    const save = async () => {
      try {
        const result = await updateScreenerSettings(desiredSettings)
        if (!result.ok) throw new Error(result.error || 'Failed to save screener preferences')
        return true
      } catch (err) {
        if (revision === settingsRevisionRef.current) setPreferenceError(
          `Chart selection is active; preferences were not saved: ${err instanceof Error ? err.message : 'Save failed'}`)
        return false
      }
    }
    const pending = settingsQueueRef.current.then(save, save)
    settingsQueueRef.current = pending
    return pending
  }, [])

  // Recover a transient bootstrap failure without retrying mutations or
  // hiding the current error. Each read settles before its backoff begins.
  useEffect(() => {
    if (hasLoadedTickers) return
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | null = null
    let delay = 1000
    const bootstrap = async () => {
      const outcome = await fetchTickers(true, controller.signal)
      if (controller.signal.aborted || outcome !== 'retry') return
      timer = setTimeout(bootstrap, delay)
      delay = Math.min(delay * 2, 30_000)
    }
    void bootstrap()
    return () => {
      controller.abort()
      if (timer !== null) clearTimeout(timer)
    }
  }, [fetchTickers, hasLoadedTickers])

  // Reject outdated requests when optional orderbook data changes, but keep the
  // current charts mounted while the replacement request is in flight.
  useLayoutEffect(() => {
    activeQueryContextRef.current = dataQueryContextKey
  }, [dataQueryContextKey])

  // Never merge chart history across ticker, interval, timeframe, or chart filters.
  useLayoutEffect(() => {
    const cached = activeTicker ? responseCacheRef.current.get(chartDataContextKey,
      includesNow ? 30_000 : 300_000, Date.now()) : null
    activeDataContextRef.current = cached ? chartDataContextKey : null
    setActiveTickerData(cached)
    lastCvdSnapshotRef.current = 0
    lastOrderbookSnapshotRef.current = 0
    lastTailAtRef.current = cached ? Date.now() : 0
    lastReconciliationRef.current = cached ? Date.now() : 0
    priceFallbackNeededRef.current = false
    optionalMetricsNeededRef.current = (cached?.pending_metrics || []) as ScreenerMetric[]
    orderbookInitialPendingRef.current = includeOrderbook
    enrichmentImmediateRef.current = true
    setLastUpdated(cached?.updated_at ? new Date(cached.updated_at * 1000) : null)
  }, [chartDataContextKey])

  // Poll for data when there's an active ticker
  useEffect(() => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    if (oiPollTimeoutRef.current) clearTimeout(oiPollTimeoutRef.current)
    fullAbortRef.current?.abort()
    oiAbortRef.current?.abort()
    fullSequenceRef.current += 1
    oiSequenceRef.current += 1

    let cancelled = false
    let fullInFlight = false
    let oiInFlight = false
    const visibilityDocument = typeof document === 'undefined' ? null : document
    let visible = visibilityDocument?.visibilityState !== 'hidden'
    const pollLiveOI = includesNow && chartScopeActive && metrics.some(metric => metric !== 'cvd')
    const pollFullContinuously = (includesNow && chartScopeActive && metrics.length > 0) || includeOrderbook
    const pollFull = async () => {
      if (cancelled || !visible || fullInFlight) return
      fullInFlight = true
      pollTimeoutRef.current = null
      try {
        await fetchActiveTickerData(activeDataContextRef.current === chartDataContextKey)
      } finally {
        fullInFlight = false
        const enrichmentPending = priceFallbackNeededRef.current || optionalMetricsNeededRef.current.length > 0
          || orderbookInitialPendingRef.current
        if (!cancelled && visible && (pollFullContinuously || enrichmentPending)) {
          let delay = POLL_INTERVAL
          if (activeDataContextRef.current === chartDataContextKey) {
            if (includesNow && metrics.includes('cvd') && lastCvdSnapshotRef.current) {
              delay = Math.min(delay, Math.max(100, lastCvdSnapshotRef.current + cvdSnapshotCadence() - Date.now()))
            }
            if (includeOrderbook && lastOrderbookSnapshotRef.current) {
              delay = Math.min(delay, Math.max(100, lastOrderbookSnapshotRef.current + POLL_INTERVAL - Date.now()))
            }
          }
          if (lastAuxiliaryFailureRef.current) delay = Math.max(delay,
            POLL_INTERVAL - (Date.now() - lastAuxiliaryFailureRef.current))
          pollTimeoutRef.current = setTimeout(pollFull, enrichmentPending && enrichmentImmediateRef.current ? 0 : delay)
        }
        if (!cancelled && visible && pollLiveOI && !oiInFlight && !oiPollTimeoutRef.current
          && activeDataContextRef.current === chartDataContextKey) {
          void pollOI()
        }
      }
    }
    const pollOI = async () => {
      if (cancelled || !visible || oiInFlight) return
      oiInFlight = true
      oiPollTimeoutRef.current = null
      const controller = new AbortController()
      oiAbortRef.current = controller
      try {
        await fetchActiveOIDelta(controller.signal)
      } finally {
        oiInFlight = false
        if (!cancelled && visible) {
          const interval = dataRef.current?.oi_interval || settings.period
          oiPollTimeoutRef.current = setTimeout(pollOI, interval === '5s' ? OI_DELTA_POLL_INTERVAL : POLL_INTERVAL)
        }
      }
    }
    const startPolling = () => {
      if (!activeTicker) return
      void pollFull()
      // A delta cannot be merged until the matching full chart exists. Starting
      // it earlier competes with initial load and can show a discarded read's timeout.
      if (pollLiveOI && activeDataContextRef.current === chartDataContextKey) void pollOI()
    }
    const onVisibilityChange = () => {
      const nextVisible = visibilityDocument?.visibilityState !== 'hidden'
      if (visible === nextVisible) return
      visible = nextVisible
      if (visible) {
        startPolling()
      } else {
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
        if (oiPollTimeoutRef.current) clearTimeout(oiPollTimeoutRef.current)
        pollTimeoutRef.current = null
        oiPollTimeoutRef.current = null
        // Let in-flight reads finish: aborting fetch does not cancel server SQL,
        // and returning to the tab could otherwise repeat that expensive work.
      }
    }
    visibilityDocument?.addEventListener('visibilitychange', onVisibilityChange)
    startPolling()

    return () => {
      cancelled = true
      visibilityDocument?.removeEventListener('visibilitychange', onVisibilityChange)
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
      if (oiPollTimeoutRef.current) clearTimeout(oiPollTimeoutRef.current)
      pollTimeoutRef.current = null
      oiPollTimeoutRef.current = null
      fullAbortRef.current?.abort()
      oiAbortRef.current?.abort()
    }
  }, [
    activeTicker,
    fetchActiveTickerData,
    fetchActiveOIDelta,
    dataQueryContextKey,
    settings.period,
    settings.timeframe?.mode,
    includeOrderbook,
    includesNow,
    chartScopeActive,
    metricKey,
    cvdSnapshotCadence,
  ])

  return {
    tickers,
    hasLoadedTickers,
    activeTicker,
    activeTickerData,
    settings,
    isLoading,
    error: preferenceError || error,
    lastUpdated,
    addTicker,
    removeTicker,
    setActiveTicker,
    updateSettings: updateSettingsAction,
    refresh,
  }
}

// Helper to aggregate candles from multiple exchanges
export function aggregateCandles(
  exchangesData: Record<string, { candles: ScreenerCandle[] }>
): ScreenerCandle[] {
  // Get all unique timestamps
  const timestampMap = new Map<number, {
    open: number[]
    high: number[]
    low: number[]
    close: number[]
    volume: number[]
  }>()

  for (const exchangeData of Object.values(exchangesData)) {
    for (const candle of exchangeData.candles || []) {
      if (!timestampMap.has(candle.timestamp)) {
        timestampMap.set(candle.timestamp, {
          open: [],
          high: [],
          low: [],
          close: [],
          volume: [],
        })
      }
      const entry = timestampMap.get(candle.timestamp)!
      entry.open.push(candle.open)
      entry.high.push(candle.high)
      entry.low.push(candle.low)
      entry.close.push(candle.close)
      entry.volume.push(candle.volume)
    }
  }

  // Aggregate into single candles (average prices, sum volumes)
  const aggregated: ScreenerCandle[] = []
  for (const [timestamp, data] of timestampMap) {
    const avgOpen = data.open.reduce((a, b) => a + b, 0) / data.open.length
    const avgHigh = Math.max(...data.high)
    const avgLow = Math.min(...data.low)
    const avgClose = data.close.reduce((a, b) => a + b, 0) / data.close.length
    const totalVolume = data.volume.reduce((a, b) => a + b, 0)

    aggregated.push({
      timestamp,
      open: avgOpen,
      high: avgHigh,
      low: avgLow,
      close: avgClose,
      volume: totalVolume,
    })
  }

  // Sort by timestamp
  aggregated.sort((a, b) => a.timestamp - b.timestamp)

  return aggregated
}

// Helper to build OI history from current values
export function buildOIHistory(
  exchangesData: Record<string, {
    oi_current: number | null
    oi_current_usdt?: number | null
    oi_history?: Array<{ timestamp: number; value: number; valueUsdt?: number | null }>
    candles: ScreenerCandle[]
  }>
): Array<{ timestamp: number; value: number; valueUsdt?: number }> {
  const oiHistoryMap = new Map<number, { value: number; valueUsdt: number }>()

  // Prefer backend-provided OI history if available.
  for (const data of Object.values(exchangesData)) {
    const history = data.oi_history || []
    if (history.length === 0) continue
    for (const point of history) {
      const timestamp = Number(point.timestamp || 0)
      if (!timestamp) continue
      const existing = oiHistoryMap.get(timestamp) || { value: 0, valueUsdt: 0 }
      existing.value += Number(point.value || 0)
      existing.valueUsdt += Number(point.valueUsdt || 0)
      oiHistoryMap.set(timestamp, existing)
    }
  }

  if (oiHistoryMap.size > 0) {
    return Array.from(oiHistoryMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([timestamp, values]) => ({
        timestamp,
        value: values.value,
        valueUsdt: values.valueUsdt > 0 ? values.valueUsdt : undefined,
      }))
  }

  // Fallback: synthesize flat series from current OI and candle timestamps.
  let totalOI = 0
  let totalOIUsdt = 0
  for (const data of Object.values(exchangesData)) {
    if (data.oi_current) totalOI += data.oi_current
    if (data.oi_current_usdt) totalOIUsdt += data.oi_current_usdt
  }
  const allCandles = Object.values(exchangesData).flatMap(d => d.candles || [])
  if (allCandles.length === 0 || (totalOI <= 0 && totalOIUsdt <= 0)) {
    return []
  }
  return allCandles.slice(-40).map(c => ({
    timestamp: c.timestamp,
    value: totalOI,
    valueUsdt: totalOIUsdt > 0 ? totalOIUsdt : undefined,
  }))
}
